///
/// EarthBrowser
///
/// Created by Matt Giger on 9/17/2026
/// Copyright (c) 2026 Geodata Labs
///
/// My Places list for the Views menu, mirroring SavedViewsList: search,
/// sort (Recent / A-Z / Nearby), fly-to on click, two-step delete. Rows
/// show the placemark's accent color dot and member count instead of a
/// thumbnail - placemarks are places, not camera poses.
///

import { sendDatasetAction } from "@/redux/earthBrowserSlice"
import { setMyPlacesSortMode, type DialogState } from "@/redux/dialogSlice"
import {
	cPlacemarkColors,
	deletePlacemark,
	placemarkAnchor,
	type Placemark,
} from "@/redux/placemarksSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { recordRecentCommand } from "@/redux/userInterfaceSlice"
import { cameraLatLonDeg, haversineKm, relativeTime } from "@/utils/savedViews"
import { cn } from "@/utils/utils"
import { ArrowDownAZ, Clock, Navigation, Search, Trash2 } from "lucide-react"
import { useCallback, useMemo, useState } from "react"

type SortMode = DialogState["myPlacesSortMode"]

const cSortOptions: { mode: SortMode; label: string; icon: React.ReactNode }[] = [
	{ mode: "recent", label: "Recent", icon: <Clock className="w-3 h-3" /> },
	{ mode: "alpha", label: "A–Z", icon: <ArrowDownAZ className="w-3 h-3" /> },
	{ mode: "nearby", label: "Nearby", icon: <Navigation className="w-3 h-3" /> },
]

function distanceLabel(km: number): string {
	if (km < 1) return "here"
	if (km < 100) return `${Math.round(km)} km away`
	return `${Math.round(km / 100) * 100} km away`
}

interface PlaceRowProps {
	placemark: Placemark
	subtitle: string
	onFlyTo: (id: string) => void
}

function PlaceRow({ placemark, subtitle, onFlyTo }: PlaceRowProps) {
	const dispatch = useAppDispatch()
	const [confirmingDelete, setConfirmingDelete] = useState(false)
	const memberCount = placemark.locations.length

	// Two-step delete: first click arms (icon turns red), second deletes.
	// Arming resets when the pointer leaves the row.
	const handleDelete = (e: React.MouseEvent) => {
		e.stopPropagation()
		if (!confirmingDelete) {
			setConfirmingDelete(true)
			return
		}
		dispatch(deletePlacemark(placemark.id))
	}

	return (
		<div
			onClick={() => onFlyTo(placemark.id)}
			onMouseLeave={() => setConfirmingDelete(false)}
			role="button"
			tabIndex={0}
			onKeyDown={e => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault()
					onFlyTo(placemark.id)
				}
			}}
			className={cn(
				"group/row flex items-center gap-2.5 w-full px-2 py-1.5 rounded-lg text-left cursor-pointer",
				"hover:bg-white/10 active:bg-white/20 transition-colors duration-200",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
			)}
			title={`Fly to "${placemark.name}"`}
		>
			<div className="relative w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">
				<span
					className="w-2.5 h-2.5 rounded-full"
					style={{ backgroundColor: cPlacemarkColors[placemark.color] ?? cPlacemarkColors.red }}
				/>
			</div>
			<div className="flex-1 min-w-0">
				<div className="text-sm text-white/85 truncate">{placemark.name}</div>
				<div className="text-[10px] text-white/40 truncate">
					{memberCount > 1 ? `${memberCount} locations · ${subtitle}` : subtitle}
				</div>
			</div>
			<button
				onClick={handleDelete}
				className={cn(
					"w-6 h-6 rounded-md flex items-center justify-center transition-colors duration-200 flex-shrink-0",
					"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
					confirmingDelete
						? "text-red-400 bg-red-500/20 hover:bg-red-500/30"
						: "text-white/35 hover:text-red-400 hover:bg-white/10"
				)}
				title={confirmingDelete ? "Click again to delete" : "Delete placemark"}
				aria-label={confirmingDelete ? `Confirm delete ${placemark.name}` : `Delete ${placemark.name}`}
			>
				<Trash2 className="w-3.5 h-3.5" />
			</button>
		</div>
	)
}

interface MyPlacesListProps {
	/** Close the hosting menu after an action */
	onDone: () => void
}

/** Search + sort + rows */
export function MyPlacesList({ onDone }: MyPlacesListProps) {
	const dispatch = useAppDispatch()
	const placemarks = useAppSelector(state => state.placemarks.placemarks)
	const sortMode = useAppSelector(state => state.dialog.myPlacesSortMode)
	const cameraState = useAppSelector(state => state.earthbrowser.cameraState)
	const [search, setSearch] = useState("")

	const sorted = useMemo(() => {
		const query = search.trim().toLowerCase()
		const list = Object.values(placemarks).filter(placemark =>
			!query ||
			placemark.name.toLowerCase().includes(query) ||
			placemark.tags.some(tag => tag.toLowerCase().includes(query)) ||
			placemark.locations.some(location => location.name?.toLowerCase().includes(query))
		)

		if (sortMode === "alpha") {
			return list
				.sort((a, b) => a.name.localeCompare(b.name))
				.map(placemark => ({ placemark, subtitle: relativeTime(placemark.updatedAt) }))
		}

		if (sortMode === "nearby") {
			const here = cameraLatLonDeg(cameraState.theta, cameraState.phi)
			return list
				.map(placemark => {
					const anchor = placemarkAnchor(placemark)
					return { placemark, km: haversineKm(here.lat, here.lon, anchor.lat, anchor.lon) }
				})
				.sort((a, b) => a.km - b.km)
				.map(({ placemark, km }) => ({ placemark, subtitle: distanceLabel(km) }))
		}

		return list
			.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
			.map(placemark => ({ placemark, subtitle: relativeTime(placemark.updatedAt) }))
	}, [placemarks, search, sortMode, cameraState.theta, cameraState.phi])

	const handleFlyTo = useCallback((id: string) => {
		// Track in Recently Used (resolved against usePlacemarkCommands at
		// render time, so deleted placemarks drop out)
		const placemark = placemarks[id]
		if (placemark) {
			dispatch(recordRecentCommand({
				id: `placemark-${id}`,
				type: "placemark",
				title: placemark.name,
				subtitle: "My place",
			}))
		}
		dispatch(sendDatasetAction({ type: "placemark", action: "selectId", id }))
		onDone()
	}, [dispatch, placemarks, onDone])

	const empty = Object.keys(placemarks).length === 0

	return (
		<div className="flex flex-col gap-1.5 min-h-0">
			<div className="relative flex-shrink-0">
				<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40 pointer-events-none" />
				<input
					value={search}
					onChange={e => setSearch(e.target.value)}
					placeholder="Search places…"
					className={cn(
						"w-full pl-8 pr-2 py-1.5 text-sm rounded-lg",
						"bg-white/5 text-white border border-white/10",
						"focus:outline-none focus:border-cyan-500/50 placeholder:text-white/40"
					)}
				/>
			</div>

			<div className="flex gap-1 flex-shrink-0" role="radiogroup" aria-label="Sort places">
				{cSortOptions.map(option => (
					<button
						key={option.mode}
						onClick={() => dispatch(setMyPlacesSortMode(option.mode))}
						role="radio"
						aria-checked={sortMode === option.mode}
						className={cn(
							"flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[11px] rounded-full",
							"transition-colors duration-200",
							"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
							sortMode === option.mode
								? "bg-rose-500/20 text-rose-300 border border-rose-500/70"
								: "bg-white/5 text-white/50 border border-transparent hover:text-white hover:bg-white/10"
						)}
					>
						{option.icon}
						{option.label}
					</button>
				))}
			</div>

			<div className="flex-1 min-h-0 overflow-y-auto">
				{sorted.length > 0 ? (
					sorted.map(({ placemark, subtitle }) => (
						<PlaceRow key={placemark.id} placemark={placemark} subtitle={subtitle} onFlyTo={handleFlyTo} />
					))
				) : (
					<p className="px-2 py-3 text-xs text-white/40 text-center">
						{empty
							? "No places yet — focus something and tap the pin icon to save it"
							: "No places match your search"}
					</p>
				)}
			</div>
		</div>
	)
}
